import React, { useState, useCallback, useEffect } from "react";
import Helmet from "react-helmet";
import { withPrefix } from "gatsby";
import PropTypes from "prop-types";
import useOnceEffect from "@/mixins/useOnceEffect";
import useRefEventListener from "@/mixins/useRefEventListener";
import Loader from "@/modules/Multi/components/Loader";
import cameraIcon from "@/assets/images/camera-icon.png";
import locale from "@/mixins/locale";

const localeArr = "modules.components.ImageTaker.Camera";

const minSwipeDistance = 50;
let listOfCanvases = [];
let gifTimeout;

let timeout = null;
let secondaryTimeout = null;
let video = null;
let webcamStream = null;
let facingMode = "user";
let touchStart = null;
let touchEnd = null;
let swipeOverlay = null;

const messages = [
	"",
	[locale(localeArr + ".cameraloading")],
	[locale(localeArr + ".errorLoading"), locale(localeArr + ".devicePermission")],
	[locale(localeArr + ".failedPicture"), locale(localeArr + ".tryAgain")]
];

const Camera = props => {
	const [overlayIdx, setOverlayIdx] = useState(0);
	const [camera, setCamera] = useState({
		width: 0,
		height: 0
	});
	const [switchingCam, setSwitchingCam] = useState(false);
	const [showMessage, setShowMessage] = useState(1);
	const [takingGif, setTakingGif] = useState(false);
	const [gifLoading, setGifLoading] = useState(false);
	// Camera can crash on fast switches
	// this is used to delay the switch to switch time
	const [camSwitchCooldown, setCamSwitchCooldown] = useState(false);

	useOnceEffect(
		useCallback(() => {
			handleHeader(true);

			swipeOverlay = document.querySelector("#swipeOverlay");

			if (props.content.showOverlays) {
				document.querySelector("#stream-holder-parent").addEventListener("touchmove", onTouchMove, {
					passive: false
				});
			}

			initializeStream();
		}, [])
	);

	useEffect(() => {
		// This replicates componentWillUnmount hook
		return () => {
			document.removeEventListener("camera-turn", camTurnListener);

			clearTimeout(timeout);
			clearTimeout(secondaryTimeout);
			stopCamera();

			handleHeader(false);
		};
	}, []);

	const aspectRatio = () => {
		const ratio = props.content.aspectRatio.split(":");

		return {
			ratio,
			positive: (ratio[1] / ratio[0]).toFixed(2) - 0.01,
			negative: (ratio[0] / ratio[1]).toFixed(2)
		}
	}
	const camTurnListener = () => {
		camTurnListenerRef.current();
	};
	const camTurnListenerRef = useRefEventListener(() => {
		switchCamera();
	});

	const handleHeader = (onMount = false) => {
		const button = document.querySelector("#camera-turn-holder");
		if (!button) {
			return;
		}
		const image = button.querySelector("img");
		const nextButton = document.querySelector("#next-button-holder");

		if (onMount) {
			image.src = image.getAttribute("pre-src");
			button.classList.remove("hidden");
			if (nextButton) {
				nextButton.classList.add("hidden");
			}

			document.addEventListener("camera-turn", camTurnListener);
		} else {
			button.classList.add("hidden");
			image.src = null;
			if (nextButton) {
				nextButton.classList.remove("hidden");
			}
		}
	};

	const switchCamera = () => {
		if (camSwitchCooldown) {
			return;
		}

		if (!camera.width) {
			return;
		}

		if (takingGif) {
			return;
		}

		if (switchingCam) {
			return;
		}

		setCamSwitchCooldown(true);
		setTimeout(() => {
			setCamSwitchCooldown(false);
		}, 1500);

		setSwitchingCam(true);
		facingMode = facingMode === "user" ? "environment" : "user";
		document.querySelector("#stream-holder").pause();
		initializeStream(true);
	};

	const playStream = () => {
		try {
			document.querySelector("#stream-holder").play();
			setShowMessage(0);
			secondaryTimeout = setTimeout(() => {
				setSwitchingCam(false);
			}, 350);
		} catch (e) {
			console.error(e);
			setShowMessage(2);
		}
	};

	const initStreamHolder = (stream, fromSwitch) => {
		try {
			const camSettings = stream.getTracks()[0].getSettings();
			if (!fromSwitch) {
				initializeVideoHolder();
			}

			video.srcObject = stream;
			webcamStream = stream;

			const cam = camera;
			cam.width = camSettings.width;
			cam.height = camSettings.height;

			setCamera(cam);
			playStream();
		} catch (e) {
			setShowMessage(2);
		}
	};

	const initializeStream = fromSwitch => {
		if (!navigator) {
			return;
		}

		if (fromSwitch) {
			stopCamera();
		}

		if (!fromSwitch) {
			navigator.getUserMedia = navigator.getUserMedia || navigator.webkitGetUserMedia || navigator.mozGetUserMedia || navigator.msGetUserMedia;
		}

		const constraints = {
			video: {
				facingMode: facingMode,
				height: { ideal: 780 },
				width: { ideal: 780 }
			},
			audio: false
		};

		if (!navigator.getUserMedia) {
			if (navigator.mediaDevices) {
				navigator.mediaDevices
					.getUserMedia(constraints)
					.then(stream => {
						initStreamHolder(stream, fromSwitch);
					})
					.catch(err => {
						console.error(err);
						setShowMessage(2);
						setSwitchingCam(false);
					});
			}

			return false;
		}

		if (!navigator) {
			return;
		}
		navigator.getUserMedia(
			constraints,
			stream => {
				initStreamHolder(stream, fromSwitch);
			},
			() => {
				setShowMessage(2);
				setSwitchingCam(false);
			}
		);
	};

	const takeSnapshot = async () => {
		try {
			if (showMessage) {
				return;
			}
			const origHeight = camera.height;
			const origWidth = camera.width;

			let height = origHeight;
			let width = height * aspectRatio().negative;
			do {
				height = height - 1;
				width = height * aspectRatio().negative;
			} while (width > origWidth);

			const canvas = document.createElement("canvas");
			canvas.height = height;
			canvas.width = width;

			const ctx = canvas.getContext("2d");

			if (facingMode === "user") {
				ctx.translate(canvas.width, 0);
				ctx.scale(-1, 1);
			}

			ctx.drawImage(video, (origWidth - width) / 2, 0, width, height, 0, 0, width, height);

			if (props.content.isGif) {
				const cnv = document.createElement("canvas");
				const cnvCtx = cnv.getContext("2d");

				cnv.width = canvas.width;
				cnv.height = canvas.height;

				cnvCtx.globalAlpha = 1.0;
				cnvCtx.drawImage(canvas, 0, 0, canvas.width, canvas.height);
				cnvCtx.globalAlpha = 1.0;
				cnvCtx.drawImage(document.querySelector(".temp-overlay"), 0, 0, canvas.width, canvas.height);

				return cnv;
			}

			const obj = {
				img: canvas.toDataURL("image/jpeg"),
				overlayIdx: overlayIdx
			};

			props.imageTaken(obj);
		} catch (e) {
			console.error(e);
			setShowMessage(3);
		}
	};

	const takeGif = () => {
		const gifWater = document.querySelector(".gif-water");

		const givenSeconds = props.content?.gif?.length || 1;
		const fps = props.content?.gif?.framerate || 8;
		// Lower is better
		const quality = props.content?.gif?.compression || 1;

		// This is used as a deadzone to not go over the given mark
		const seconds = givenSeconds - (6 / 100) * givenSeconds;

		const timeout = 1000 / fps;
		const counter = (seconds * 1000) / timeout;

		let i = 0;

		const drawImage = () => {
			clearTimeout(gifTimeout);
			setGifLoading(true);

			document.querySelector("#stream-holder").style.display = "none";
			const gif = new window.GIF({
				workers: navigator?.hardwareConcurrency || 4,
				quality: quality,
				delay: 0
			});

			listOfCanvases.forEach(item => {
				gif.addFrame(item, { delay: 1 });
			});

			gif.on("finished", function (blob) {
				const url = URL.createObjectURL(blob);
				document.querySelector("#gif-holder").src = url;
				const obj = {
					img: url,
					overlayIdx: overlayIdx,
					isGif: true
				};

				setTakingGif(false);
				setGifLoading(false);
				gifWater.style.height = `0px`;
				props.imageTaken(obj);
			});

			gif.render();

			gifWater.style.transition = null;
			gifWater.style.height = `100%`;
		};

		const addFrames = async () => {
			const img = await takeSnapshot();
			listOfCanvases.push(img);
			if (i++ < counter) {
				gifTimeout = setTimeout(addFrames, timeout);
				return;
			}
			drawImage();
		};

		if (takingGif) {
			return drawImage();
		}

		listOfCanvases = [];
		setTakingGif(true);
		addFrames();
		gifWater.style.height = `100%`;
		gifWater.style.transition = `${seconds}s linear`;
	};

	const getCameraDimensions = () => {
		const cameraHolderWidth = document.querySelector("#camera-holder").clientWidth;
		const width = cameraHolderWidth > 636 ? 636 : Math.ceil(cameraHolderWidth);
		const height = width * aspectRatio().positive;

		return {
			width,
			height
		};
	};

	const takeCanvasSnapshot = () => {
		const { width, height } = getCameraDimensions();

		const canvas = document.createElement("canvas");
		canvas.width = width;
		canvas.height = height;
		const obj = {
			img: canvas.toDataURL("image/jpeg"),
			overlayIdx: overlayIdx
		};

		props.imageTaken(obj);
	};

	const initializeVideoHolder = () => {
		video = document.createElement("video");
		video.setAttribute("playsinline", "playsinline");
		video.id = "stream-holder";

		const streamHolderParent = document.querySelector("#stream-holder-parent");

		if (!streamHolderParent) {
			return;
		}

		streamHolderParent.appendChild(video);

		const { width, height } = getCameraDimensions();

		if (!video) {
			return;
		}

		video.style.setProperty("height", height + "px", "important");
		video.setAttribute("height", height);
		video.setAttribute("width", width);
	};

	const stopCamera = () => {
		try {
			if (!webcamStream) {
				return;
			}
			if (!webcamStream.getTracks()) {
				return;
			}
			webcamStream.getTracks().forEach(item => item.stop());
		} catch (e) {
			console.error(e);
		}
	};

	const getOverlayPosition = position => {
		try {
			const currentIdx = overlayIdx;
			if (position === 0) {
				return currentIdx;
			}

			const ceil = props.content.overlays.length;
			let newPos = currentIdx + position;

			if (newPos >= ceil) {
				const pos = newPos - ceil;
				if (pos >= ceil) {
					return ceil - 1;
				}
				return pos;
			}
			if (newPos < 0) {
				return ceil + newPos;
			}

			return newPos;
		} catch (e) {
			console.error(e);
			return 0;
		}
	};

	const thumbToShow = position => {
		let newPos = getOverlayPosition(position);
		if (newPos < 0) {
			newPos = 0;
		}

		return props.content.overlays[newPos].thumb;
	};

	const setOverlay = position => {
		const idx = getOverlayPosition(position);

		setOverlayIdx(idx);
	};

	const setTouchEnd = e => {
		touchEnd = e;
	};

	const onTouchStart = e => {
		if (takingGif) {
			return;
		}
		setTouchEnd(null);
		touchStart = e.targetTouches[0].clientX;
	};

	const onTouchMove = e => {
		if (takingGif) {
			return;
		}
		setTouchEnd(e.targetTouches[0].clientX);
		if (!touchEnd) {
			return;
		}

		let distance = (touchStart - touchEnd) / 4;
		if (Math.abs(distance) > 1) {
			e.preventDefault();
		}

		if (Math.abs(distance) < 10) {
			return;
		}

		if (distance > 0) {
			swipeOverlay.classList.add("right-swipe");
			swipeOverlay.classList.remove("left-swipe");
			distance = 60 - distance;
			if (distance > 59 || distance < 10) {
				return;
			}
		} else {
			swipeOverlay.classList.remove("right-swipe");
			swipeOverlay.classList.add("left-swipe");
			distance = 60 - Math.abs(distance);
			distance = distance * -1;
			if (distance > -10 || distance < -59) {
				return;
			}
		}

		swipeOverlay.style.transform = `translateX(${distance}px)`;
	};

	const onTouchEnd = () => {
		if (takingGif) {
			return;
		}
		swipeOverlay.classList.remove("right-swipe");
		swipeOverlay.classList.add("left-swipe");
		swipeOverlay.style.transform = `translateX(-1000px)`;

		if (!touchStart || !touchEnd) return;

		const distance = touchStart - touchEnd;
		const isRightSwipe = distance > minSwipeDistance;
		const isLeftSwipe = distance < -minSwipeDistance;

		if (isRightSwipe) {
			setOverlay(1);
		}
		if (isLeftSwipe) {
			setOverlay(-1);
		}
	};

	const showThumbHolder = item => {
		if (props.content.isGif && takingGif) {
			return item === 0;
		}

		return true;
	};

	const numberOfOverlays = () => {
		const overlays = props.content.overlays.length;
		if (overlays > 1) {
			return [-2, -1, 0, 1, 2];
		}

		return [0];
	}

	return (
		<div id="camera-holder" className="camera relative flex flex-col items-center">
			{props.content.isGif && (
				<Helmet>
					<script src={withPrefix("gif.js")} type="text/javascript" />
				</Helmet>
			)}
			{gifLoading && <Loader absolute={false} />}
			<img src="" id="gif-holder" className="d-none" />
			<div className="relative flex items-center justify-center w-100">
				{showMessage !== 0 && (
					<div className="absolute z-1 text-center left-0 right-0 m-auto">
						<h4 className="text-xl text-cameraMsg">{messages[showMessage][0]}</h4>
						<h5 className="text-lg text-cameraMsg">{messages[showMessage][1]}</h5>
					</div>
				)}
				<div
					id="stream-holder-parent"
					onTouchStart={onTouchStart}
					onTouchEnd={onTouchEnd}
					className={`frosted-glass ${facingMode === "user" ? "facing-user" : ""}`}>
					{props.content?.showOverlays ? (
						<img src={props.content.overlays[overlayIdx].overlay} className={`temp-overlay ${showMessage ? "opacity-0" : ""}`} />
					) : (
						<img src="" className="temp-overlay" />
					)}
					<div id="swipeOverlay" className={`${showMessage ? "opacity-0" : ""}`}></div>
				</div>
			</div>

			<div className="container mt-0 mb-auto py-2">
				{props.content.overlays && (
					<div className={`row-span-full py-1 flex justify-center ${showMessage || switchingCam ? "opacity-50" : ""}`}>
						{props.content?.showOverlays &&
							numberOfOverlays().map(item => {
								return (
									showThumbHolder(item) && (
										<div key={"thumb-slider-" + item} className="col-auto flex items-center thumb-holder-parent">
											<div
												onClick={() => {
													if (item === 0) {
														props.content.isGif ? takeGif() : takeSnapshot();
													} else {
														setOverlay(item);
													}
												}}
												className={`thumb-holder flex items-center justify-center relative overflow-hidden ${
													item === 0 ? "current-thumb" : null
												}`}>
												<img src={thumbToShow(item)} />
												{item === 0 && <div className="gif-water"></div>}
											</div>
										</div>
									)
								);
							})}
						{!props.content?.showOverlays && (
							<div className="col-auto flex items-center thumb-holder-parent">
								<div
									onClick={() => {
										props.content.isGif ? takeGif() : takeSnapshot();
									}}
									className={`thumb-holder flex items-center justify-center relative overflow-hidden current-thumb no-overlays`}>
									<img src={cameraIcon} className="-singular" />
									<div className="gif-water"></div>
								</div>
							</div>
						)}
					</div>
				)}
			</div>
		</div>
	);
};

Camera.propTypes = {
	imageTaken: PropTypes.func,
	content: PropTypes.object
};

export default Camera;
